import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import Modal from '../../components/Modal';
import RegionChangeModal from './RegionChangeModal';

const REGIONS = ['Fort Worth', 'Dallas', 'Denton'];

export default function PropertyManagementPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [showRegionChange, setShowRegionChange] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  async function load() {
    try { setProperties(await api.getProperties({ search, region: regionFilter })); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [search, regionFilter]); // eslint-disable-line

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const result = await api.uploadProperties(fd);
      alert(result.message || 'Upload complete!');
      load();
    } catch (err) { setError(err.message); }
    setUploading(false);
    e.target.value = '';
  }

  async function handleDownloadTemplate() {
    try {
      const blob = await api.downloadPropertyTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'property-upload-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Failed to download template: ' + err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this property?')) return;
    try { await api.deleteProperty(id); load(); }
    catch (err) { alert(err.message); }
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h2>Property Management</h2>
        <div className="header-actions">
          <button className="btn btn-muted" onClick={handleDownloadTemplate}>
            Download Template
          </button>
          <button className="btn btn-muted" onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload Excel'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} style={{ display: 'none' }} />
          <button className="btn btn-muted" onClick={() => setShowRegionChange(true)}>Change Regions</button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Add Property</button>
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="filter-row" style={{ marginBottom: 16 }}>
        <input placeholder="Search properties..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
          <option value="">All Regions</option>
          {REGIONS.map(r => <option key={r}>{r}</option>)}
        </select>
      </div>

      {loading ? <div className="loading">Loading...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Address</th><th>City</th><th>Zip</th><th>Region</th><th>Access Code</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {properties.map(p => (
                <tr key={p.id}>
                  <td>
                    <Link
                      to={`/sales/properties/${p.id}`}
                      style={{ color: '#3F66A0', fontWeight: 600, textDecoration: 'none' }}
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td>{p.address}</td>
                  <td>{p.city}</td>
                  <td>{p.zip}</td>
                  <td>{p.region}</td>
                  <td>{p.access_code ? <span className="access-code-badge">{p.access_code}</span> : <span className="text-muted">—</span>}</td>
                  <td>
                    <button className="btn btn-sm btn-muted" onClick={() => setEditProp(p)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                  </td>
                </tr>
              ))}
              {properties.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted">No properties found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <PropertyFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {showRegionChange && (
        <RegionChangeModal onClose={() => setShowRegionChange(false)} onDone={load} />
      )}
      {editProp && (
        <PropertyFormModal
          property={editProp}
          onClose={() => setEditProp(null)}
          onSaved={() => { setEditProp(null); load(); }}
        />
      )}
    </div>
  );
}

function PropertyFormModal({ property, onClose, onSaved }) {
  const isEdit = !!property;
  const [form, setForm] = useState({
    name: property?.name || '',
    address: property?.address || '',
    city: property?.city || '',
    zip: property?.zip || '',
    region: property?.region || 'Fort Worth',
    access_code: property?.access_code || '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (isEdit) await api.updateProperty(property.id, form);
      else await api.createProperty(form);
      onSaved();
    } catch (err) { setError(err.message); }
    setSubmitting(false);
  }

  return (
    <Modal title={isEdit ? 'Edit Property' : 'Add Property'} onClose={onClose}>
      {error && <div className="alert alert-error">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Property Name</label>
          <input value={form.name} onChange={e => set('name', e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Address</label>
          <input value={form.address} onChange={e => set('address', e.target.value)} required />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>City</label>
            <input value={form.city} onChange={e => set('city', e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Zip Code</label>
            <input value={form.zip} onChange={e => set('zip', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Region</label>
            <select value={form.region} onChange={e => set('region', e.target.value)}>
              {REGIONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Public Form Access Code</label>
          <input value={form.access_code} onChange={e => set('access_code', e.target.value)} placeholder="e.g. APT2024" maxLength={50} />
          <small className="text-muted">Residents use this code to submit space calls online at /request</small>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-muted" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Property')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
